import { ActiveUsersLineChart } from "@/components/insights/ActiveUsersLineChart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { TimeRangeSelector } from "@/components/TimeRangeSelector";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useListProjects } from "@/queries/project-queries";
import { parseAsTimeRange, parseAsView, TIME_RANGES, type TimeRange, type View } from "@/routes/insights/insights";
import { InsightCards } from "@/components/insights/InsightsCardRibbon";
import { InsightsTopTablesList } from "@/components/insights/InsightsTopTablesList";
import { Timestamp } from "@bufbuild/protobuf";
import { useQueryState } from "nuqs";
import { type FC, useMemo, useState } from "react";

export const InsightsPage: FC = () => {
    useDocumentTitle("Insights");

    const [selectedRange, setSelectedRangeState] = useQueryState(
        "selectedRange",
        parseAsTimeRange.withDefault(TIME_RANGES[1]),
    ); // Default to "Last Week"

    // Create a wrapper function to handle the type conversion
    const setSelectedRange = (range: TimeRange) => {
        // Find the matching time range from TIME_RANGES
        const matchingRange = TIME_RANGES.find((r) => r.value === range.value);
        if (matchingRange) {
            void setSelectedRangeState(matchingRange);
        }
    };

    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const { data: projects, isPending: isPendingProjects } = useListProjects();

    const startTime = useMemo(
        () => new Date(new Date().setDate(new Date().getDate() - selectedRange.days)),
        [selectedRange],
    );
    const endTime = useMemo(() => new Date(), []);

    const startTimestamp = useMemo(() => Timestamp.fromDate(startTime), [startTime]);
    const endTimestamp = useMemo(() => Timestamp.fromDate(endTime), [endTime]);

    const [selectedView, setSelectedView] = useQueryState<View>(
        "view",
        parseAsView.withDefault("activeUsers").withOptions({ clearOnDefault: false }),
    );

    return (
        <div data-testid="insights-page" className="flex-col items-center gap-6">
            <div data-testid="insights-page-contents" className="contents">
                <Heading1>Insights</Heading1>

                {/* Global Time Range Selector */}
                <div className="mb-4 mt-4 flex w-full items-center gap-4">
                    <TimeRangeSelector
                        timeRanges={[...TIME_RANGES]}
                        selectedRange={selectedRange}
                        setSelectedRange={setSelectedRange}
                    />
                    for
                    <div className="w-[160px]">
                        <Select name="project" value={selectedProject || ""} onValueChange={setSelectedProject}>
                            <SelectTrigger
                                loading={false}
                                disabled={isPendingProjects || projects?.projects.length === 0}
                                data-testid="prompt-input-project-select"
                            >
                                <SelectValue placeholder={"All Projects"} />
                            </SelectTrigger>
                            <SelectContent>
                                {(projects?.projects ?? []).map((project) => (
                                    <SelectItem
                                        key={project.id}
                                        value={project.id}
                                        data-testid={`prompt-input-project-option-${project.id}`}
                                    >
                                        <div>{project.metadata?.name || project.id}</div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Usage Cards */}
                <InsightCards
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    selectedRange={selectedRange}
                    selectedView={selectedView}
                    handleChangeView={(view) => setSelectedView(view as View)}
                />

                {/* Line Chart */}
                <div className="mt-6 w-full rounded-xl bg-surface-01">
                    <ActiveUsersLineChart
                        startTimestamp={startTimestamp}
                        endTimestamp={endTimestamp}
                        selectedRange={selectedRange}
                    />
                </div>

                <div className="my-6">
                    <InsightsTopTablesList
                        startTimestamp={startTimestamp}
                        endTimestamp={endTimestamp}
                        selectedRange={selectedRange}
                    />
                </div>
            </div>
        </div>
    );
};
